import { useRememberDocument } from '@/contexts/DocumentWorkspace';
import { useIncomingDocument } from '@/contexts/DocumentWorkspace';
import { useState, useCallback, useEffect, useRef } from 'react';
import { useWorkspaceNavigate as useNavigate } from '@/contexts/DocumentWorkspace';
import {
  Upload,
  PenTool,
  Type,
  Calendar,
  X,
  Loader2,
  CheckCircle2,
  Download,
} from 'lucide-react';
import { pdfjsLib } from '@/lib/pdfWorker';
import { readPdfLibBytes, pdfIntakeErrorMessage } from '@/lib/pdfIntake';
import { saveViaWorker } from '@/lib/saveWorkerClient';
import { overlaysToEdits, signSaveOptions } from '@/lib/signSave';
import { useFileDialog } from '@/hooks/useFileDialog';
import CloudSourceHint from '@/components/CloudSourceHint';
import { usageCapApplies, hasReachedLimit, incrementUsage } from '@/lib/webUsage';
import FreeTierBanner from '@/components/FreeTierBanner';
import PageCanvas from '@/components/PageCanvas';
import SignaturePanel from '@/components/SignaturePanel';

let overlayIdCounter = 0;

/**
 * Sign — Add signatures, dates, and text to a PDF.
 *
 * Upload a PDF → create a signature (draw/type/upload) → click pages to
 * place overlays → drag/resize to position → apply and save.
 */
export default function Sign() {
  const navigate = useNavigate();
  const rememberDocument = useRememberDocument();
  const { openFile, saveFile, desktop } = useFileDialog();

  // --- File state ---
  const [pdfDoc, setPdfDoc] = useState(null);
  const [arrayBuffer, setArrayBuffer] = useState(null);
  const [fileName, setFileName] = useState('');
  const [fileSize, setFileSize] = useState(0);
  const [numPages, setNumPages] = useState(0);
  const [pageSizes, setPageSizes] = useState([]); // [{ w, h }] viewport at scale=1
  const [isDragging, setIsDragging] = useState(false);

  // --- Signature state ---
  const [signatureDataUrl, setSignatureDataUrl] = useState(null);
  const [showPanel, setShowPanel] = useState(false);

  // --- Placement state ---
  const [placementMode, setPlacementMode] = useState(null); // { type, data }
  const [overlays, setOverlays] = useState([]);
  const [selectedId, setSelectedId] = useState(null);

  // --- Free tier ---
  const [showFreeTierModal, setShowFreeTierModal] = useState(false);

  // --- Drag/resize state ---
  const [dragState, setDragState] = useState(null);

  // --- Text/date input ---
  const [textInput, setTextInput] = useState('');
  const [dateInput, setDateInput] = useState(
    new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
  );
  const [showTextInput, setShowTextInput] = useState(null); // 'date' | 'text' | null

  // --- Processing ---
  const [processing, setProcessing] = useState(false);
  const saveInFlight = useRef(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [signedFile, setSignedFile] = useState(null); // { blob, name } after a successful save/download

  // A saved copy only represents the placements used to generate it. Editing
  // them must restore the save action instead of offering an outdated copy.
  useEffect(() => {
    setSignedFile(null);
    setNotice(null);
  }, [overlays]);

  // ==================== File handling ====================

  const loadFile = useCallback(
    async (file) => {
      if (saveInFlight.current) return;
      if (!file || file.type !== 'application/pdf' && !/\.pdf$/i.test(file.name)) return;
      if (pdfDoc) pdfDoc.destroy();

      setError(null);
      setNotice(null);
      setSignedFile(null);
      setPlacementMode(null);
      setShowTextInput(null);
      setOverlays([]);
      setSelectedId(null);
      setFileName(file.name);
      setFileSize(file.size);

      try {
        // Decrypt owner-password PDFs (lossless) / reject truly locked ones
        // before pdf-lib ever sees the bytes — see src/lib/pdfIntake.js.
        const buf = await readPdfLibBytes(file);
        setArrayBuffer(buf);
        const doc = await pdfjsLib.getDocument({ data: buf.slice(0) }).promise;
        setPdfDoc(doc);
        rememberDocument(file);
        setNumPages(doc.numPages);

        // Get page sizes for coordinate math
        const sizes = [];
        for (let i = 1; i <= doc.numPages; i++) {
          const page = await doc.getPage(i);
          const vp = page.getViewport({ scale: 1 });
          sizes.push({ w: vp.width, h: vp.height });
        }
        setPageSizes(sizes);
      } catch (err) {
        setError(pdfIntakeErrorMessage(err, 'sign it'));
      }
    },
    [pdfDoc, rememberDocument]
  );

  useIncomingDocument(loadFile);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    loadFile(e.dataTransfer.files[0]);
  }, [loadFile]);

  const handleBrowse = useCallback(async () => {
    const result = await openFile('.pdf');
    if (result) {
      const file = new File([result.buffer], result.name, { type: 'application/pdf' });
      loadFile(file);
    }
  }, [openFile, loadFile]);

  // ==================== Placement ====================

  const exitPlacement = useCallback(() => {
    setPlacementMode(null);
    setShowTextInput(null);
  }, []);

  const startPlaceSignature = useCallback(() => {
    if (!signatureDataUrl) {
      setShowPanel(true);
      return;
    }
    setPlacementMode({ type: 'signature', dataUrl: signatureDataUrl });
    setShowTextInput(null);
    setSelectedId(null);
  }, [signatureDataUrl]);

  const startPlaceDate = useCallback(() => {
    setShowTextInput('date');
    setPlacementMode(null);
    setSelectedId(null);
  }, []);

  const startPlaceText = useCallback(() => {
    setShowTextInput('text');
    setPlacementMode(null);
    setSelectedId(null);
  }, []);

  const confirmTextPlacement = useCallback(() => {
    const text = showTextInput === 'date' ? dateInput : textInput;
    if (!text.trim()) return;
    setPlacementMode({ type: showTextInput, text: text.trim() });
    setShowTextInput(null);
  }, [showTextInput, dateInput, textInput]);

  // Escape key exits placement mode
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && placementMode) {
        exitPlacement();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [placementMode, exitPlacement]);

  const handlePageClick = useCallback(
    (pageIndex, e) => {
      if (saveInFlight.current) return;
      if (!placementMode) {
        setSelectedId(null);
        return;
      }

      const rect = e.currentTarget.getBoundingClientRect();
      const clickX = (e.clientX - rect.left) / rect.width;
      const clickY = (e.clientY - rect.top) / rect.height;

      const isImg = placementMode.type === 'signature';
      const defaultW = isImg ? 0.25 : 0.15;
      const defaultH = isImg ? 0.06 : 0.025;

      const overlay = {
        id: `o-${++overlayIdCounter}`,
        type: placementMode.type,
        pageIndex,
        x: Math.max(0, Math.min(clickX - defaultW / 2, 1 - defaultW)),
        y: Math.max(0, Math.min(clickY - defaultH / 2, 1 - defaultH)),
        w: defaultW,
        h: defaultH,
        ...(isImg
          ? { dataUrl: placementMode.dataUrl }
          : { text: placementMode.text, fontSize: 0.018 }),
      };

      setOverlays((prev) => [...prev, overlay]);
      setSelectedId(overlay.id);
      // Stay in placement mode — user can keep clicking to place more
    },
    [placementMode]
  );

  const removeOverlay = useCallback((id) => {
    if (saveInFlight.current) return;
    setOverlays((prev) => prev.filter((o) => o.id !== id));
    setSelectedId((prev) => (prev === id ? null : prev));
  }, []);

  // ==================== Drag / Resize ====================

  const handleOverlayMouseDown = useCallback(
    (overlayId, action, e) => {
      e.stopPropagation();
      e.preventDefault();
      if (saveInFlight.current) return;
      setSelectedId(overlayId);

      // Capture the overlay's position NOW so the effect doesn't depend on overlays state
      const overlay = overlays.find((o) => o.id === overlayId);
      if (!overlay) return;

      const pageSize = pageSizes[overlay.pageIndex];
      if (!pageSize) return;

      // Get the actual rendered page container size from the DOM
      // (accounts for any CSS scaling vs raw viewport dimensions)
      const pageEl = document.querySelector(`[data-page-index="${overlay.pageIndex}"]`);
      const containerW = pageEl ? pageEl.clientWidth : pageSize.w;
      const containerH = pageEl ? pageEl.clientHeight : pageSize.h;

      setDragState({
        overlayId,
        action,
        startMouseX: e.clientX,
        startMouseY: e.clientY,
        startX: overlay.x,
        startY: overlay.y,
        startW: overlay.w,
        startH: overlay.h,
        aspect: overlay.w / overlay.h,
        containerW,
        containerH,
        type: overlay.type,
      });
    },
    [overlays, pageSizes]
  );

  useEffect(() => {
    if (!dragState) return;

    const {
      overlayId, action, startMouseX, startMouseY,
      startX, startY, startW, startH, aspect,
      containerW, containerH, type,
    } = dragState;

    const handleMouseMove = (e) => {
      if (saveInFlight.current) return;
      const dx = (e.clientX - startMouseX) / containerW;
      const dy = (e.clientY - startMouseY) / containerH;

      setOverlays((prev) =>
        prev.map((o) => {
          if (o.id !== overlayId) return o;

          if (action === 'move') {
            return {
              ...o,
              x: Math.max(0, Math.min(startX + dx, 1 - o.w)),
              y: Math.max(0, Math.min(startY + dy, 1 - o.h)),
            };
          }

          // Resize
          let newW = Math.max(0.03, startW + dx);
          let newH = type === 'signature' ? newW / aspect : Math.max(0.015, startH + dy);
          newW = Math.min(newW, 1 - startX);
          newH = Math.min(newH, 1 - startY);

          return { ...o, w: newW, h: newH };
        })
      );
    };

    const handleMouseUp = () => setDragState(null);

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragState]);

  // ==================== Commit ====================

  const handleCommit = useCallback(
    async (flatten) => {
      if (saveInFlight.current || !arrayBuffer || overlays.length === 0) return;
      // Save again reuses the confirmed output; it is not another tool task.
      const savedCopy = flatten ? signedFile : null;

      if (!savedCopy && usageCapApplies() && hasReachedLimit()) {
        setShowFreeTierModal(true);
        return;
      }

      saveInFlight.current = true;
      setProcessing(true);
      setError(null);
      setNotice(null);

      // Sign-page overlays → Scrivar edits (absolute PDF points). saveViaWorker
      // expects the same shape the Viewer uses.
      const edits = overlaysToEdits(overlays, pageSizes);

      try {
        const blob = savedCopy?.blob || new Blob([await saveViaWorker({
          arrayBuffer, edits, annotations: [], options: signSaveOptions({ flatten }),
        })], { type: 'application/pdf' });
        const baseName = fileName.replace(/\.pdf$/i, '');
        const outName = savedCopy?.name || `${baseName}_signed.pdf`;

        if (flatten) {
          // Flattening embeds the visual signature; it does not certify the
          // document or prevent editing. Native cancellation returns null.
          const destination = await saveFile(blob, outName);
          if (!destination) {
            setNotice('Save canceled. Your signature placements are still here; you can try again.');
            return;
          }
          const name = destination.split(/[\\/]/).pop() || outName;
          setSignedFile({ blob, name });
          exitPlacement();
          setSelectedId(null);
        } else {
          // Keep editable: hand off to the Viewer so the user can keep working.
          const file = new File([blob], outName, { type: 'application/pdf' });
          navigate('/viewer', { state: { file } });
        }
        if (!savedCopy && usageCapApplies()) incrementUsage();
      } catch (err) {
        setError(`Failed to ${flatten ? 'save signed PDF' : 'apply signature'}: ${err.message}`);
      } finally {
        saveInFlight.current = false;
        setProcessing(false);
      }
    },
    [arrayBuffer, overlays, pageSizes, fileName, navigate, saveFile, signedFile, exitPlacement]
  );

  const resetForAnother = useCallback(() => {
    setSignedFile(null);
    setOverlays([]);
    setSelectedId(null);
    setError(null);
    setNotice(null);
    setPlacementMode(null);
    setShowTextInput(null);
  }, []);

  // ==================== Signature panel callback ====================

  const handleSignatureCreated = useCallback((dataUrl) => {
    setSignatureDataUrl(dataUrl);
    setShowPanel(false);
    setPlacementMode({ type: 'signature', dataUrl });
  }, []);

  // ==================== Render ====================

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-3 shrink-0">
        <div className="max-w-5xl mx-auto flex items-center gap-4">
          <h1 className="text-lg font-bold text-gray-900">Sign PDF</h1>

          {pdfDoc && (
            <>
              <div className="h-6 w-px bg-gray-200 mx-1" />

              {/* Tool buttons */}
              <button
                onClick={startPlaceSignature}
                disabled={processing}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  placementMode?.type === 'signature'
                    ? 'bg-brand-blue text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <PenTool size={15} />
                Signature
              </button>
              <button
                onClick={startPlaceDate}
                disabled={processing}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  placementMode?.type === 'date' || showTextInput === 'date'
                    ? 'bg-brand-blue text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <Calendar size={15} />
                Date
              </button>
              <button
                onClick={startPlaceText}
                disabled={processing}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  placementMode?.type === 'text' || showTextInput === 'text'
                    ? 'bg-brand-blue text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <Type size={15} />
                Text
              </button>

              {signatureDataUrl && (
                <button
                  onClick={() => setShowPanel(true)}
                  disabled={processing}
                  className="text-xs text-gray-500 hover:text-gray-700 ml-1"
                >
                  Change signature
                </button>
              )}

              <div className="flex-1" />

              <button
                onClick={handleBrowse}
                disabled={processing}
                className="text-sm text-brand-blue hover:underline font-medium"
              >
                Change file
              </button>
            </>
          )}
        </div>

        {/* Text/date input bar */}
        {showTextInput && (
          <div className="max-w-5xl mx-auto mt-2 flex items-center gap-2">
            <input
              type="text"
              disabled={processing}
              value={showTextInput === 'date' ? dateInput : textInput}
              onChange={(e) =>
                showTextInput === 'date'
                  ? setDateInput(e.target.value)
                  : setTextInput(e.target.value)
              }
              onKeyDown={(e) => e.key === 'Enter' && confirmTextPlacement()}
              placeholder={showTextInput === 'date' ? 'Enter date…' : 'Enter text…'}
              className="flex-1 h-9 px-3 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-brand-blue"
              autoFocus
            />
            <button
              onClick={confirmTextPlacement}
              disabled={processing}
              className="h-9 px-4 text-sm font-medium bg-brand-blue text-white rounded-lg hover:bg-brand-dark transition-colors"
            >
              Place on page
            </button>
            <button
              onClick={() => setShowTextInput(null)}
              className="h-9 px-2 text-gray-400 hover:text-gray-600"
            >
              <X size={16} />
            </button>
          </div>
        )}
      </div>

      {error && <p role="alert" className="mx-auto w-full max-w-5xl px-6 py-3 text-red-600 text-sm">{error}</p>}
      {notice && <p role="status" className="mx-auto w-full max-w-5xl px-6 py-3 text-gray-700 text-sm">{notice}</p>}

      {/* Content */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-5xl mx-auto p-6">
          {/* Upload */}
          {!pdfDoc && (
            <>
              <div
                onDrop={handleDrop}
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
                onClick={handleBrowse}
                className={`border-2 border-dashed rounded-2xl p-10 text-center cursor-pointer transition-all mb-6 ${
                  isDragging
                    ? 'border-brand-blue bg-brand-light/30 scale-[1.01]'
                    : 'border-gray-300 hover:border-brand-blue hover:bg-gray-50'
                }`}
              >
                <Upload size={36} className={`mx-auto mb-3 ${isDragging ? 'text-brand-blue' : 'text-gray-400'}`} />
                <p className="text-lg font-medium text-gray-700">Drop a PDF here, or click to browse</p>
                <p className="text-sm text-gray-400 mt-1">Add your signature, date, or text</p>
              </div>
              <CloudSourceHint
                onPicked={(r) => loadFile(new File([r.buffer], r.name, { type: 'application/pdf' }))}
                className="mb-6"
              />
            </>
          )}

          {/* Placement banner */}
          {placementMode && (
            <div className="mb-4 px-4 py-2.5 bg-brand-light/40 border border-brand-blue/30 rounded-lg flex items-center justify-center gap-3">
              <span className="text-sm text-brand-dark">
                Click on any page to place your {placementMode.type} — click multiple pages to add more
              </span>
              <button
                onClick={exitPlacement}
                className="px-3 py-1 text-xs font-medium bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Done
              </button>
              <span className="text-xs text-gray-400">or press Esc</span>
            </div>
          )}

          {/* PDF Pages */}
          {pdfDoc && (
            <div className="flex flex-col items-center gap-4 pb-24">
              {Array.from({ length: numPages }, (_, i) => (
                <div
                  key={i}
                  data-page-index={i}
                  className={`relative shadow-lg ${placementMode ? 'cursor-crosshair' : ''}`}
                  onClick={(e) => handlePageClick(i, e)}
                  style={
                    pageSizes[i]
                      ? { width: `${pageSizes[i].w}px`, height: `${pageSizes[i].h}px` }
                      : undefined
                  }
                >
                  <PageCanvas
                    pdfDoc={pdfDoc}
                    pageNumber={i + 1}
                    scale={1}
                    className="bg-white"
                  />

                  {/* Overlays on this page */}
                  {overlays
                    .filter((o) => o.pageIndex === i)
                    .map((o) => (
                      <OverlayItem
                        key={o.id}
                        overlay={o}
                        pageSize={pageSizes[i]}
                        disabled={processing}
                        isSelected={selectedId === o.id}
                        onSelect={() => setSelectedId(o.id)}
                        onRemove={() => removeOverlay(o.id)}
                        onMouseDown={(action, e) =>
                          handleOverlayMouseDown(o.id, action, e)
                        }
                      />
                    ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Success bar — only after a confirmed save or initiated download */}
      {pdfDoc && signedFile && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-6 py-4 z-10">
          <div className="max-w-5xl mx-auto flex flex-wrap items-center justify-between gap-4">
            <div role="status" className="flex items-center gap-2 min-w-0">
              <CheckCircle2 size={18} className="text-green-600 shrink-0" />
              <p className="text-sm text-gray-700 break-words">
                {desktop ? 'Saved as ' : 'Download started for '}<span className="font-medium">{signedFile.name}</span>.
                {' '}Your signature is part of the page. The PDF can still be edited.
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => handleCommit(true)}
                disabled={processing}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                {processing ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
                {processing ? 'Saving…' : 'Save again'}
              </button>
              <button
                onClick={resetForAnother}
                disabled={processing}
                className="px-4 py-2 text-sm font-medium bg-brand-blue text-white rounded-lg hover:bg-brand-dark transition-colors"
              >
                Sign another
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Action bar — place / commit signature */}
      {pdfDoc && !signedFile && overlays.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-6 py-4 z-10">
          <div className="max-w-5xl mx-auto flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0 flex-1 basis-72">
              {processing && (
                <div className="flex items-center gap-2">
                  <Loader2 size={16} className="animate-spin text-brand-blue" />
                  <span className="text-sm text-gray-600">Saving…</span>
                </div>
              )}
              {!processing && (
                <p id="sign-save-description" className="text-sm text-gray-500">
                  {overlays.length} item{overlays.length !== 1 ? 's' : ''} placed ·{' '}
                  <span>
                    Your signature becomes part of the page. The PDF can still be edited;
                    this does not add a digital certificate.
                  </span>
                </p>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => handleCommit(false)}
                disabled={processing}
                className="px-4 py-2.5 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Keep editable
              </button>
              <button
                onClick={() => handleCommit(true)}
                disabled={processing}
                aria-describedby="sign-save-description"
                className="flex items-center gap-2 px-5 py-2.5 bg-brand-blue text-white rounded-lg font-medium hover:bg-brand-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download size={16} />
                Sign &amp; Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Signature creation panel */}
      {showPanel && (
        <SignaturePanel
          onCreated={handleSignatureCreated}
          onClose={() => setShowPanel(false)}
          fieldKind="signature"
        />
      )}

      <FreeTierBanner open={showFreeTierModal} onClose={() => setShowFreeTierModal(false)} />
    </div>
  );
}

// ==================== Overlay item ====================

function OverlayItem({ overlay, pageSize, isSelected, disabled, onSelect, onRemove, onMouseDown }) {
  if (!pageSize) return null;

  const style = {
    position: 'absolute',
    left: `${overlay.x * 100}%`,
    top: `${overlay.y * 100}%`,
    width: `${overlay.w * 100}%`,
    height: `${overlay.h * 100}%`,
  };

  return (
    <div
      style={style}
      onClick={(e) => { e.stopPropagation(); if (!disabled) onSelect(); }}
      onMouseDown={(e) => { e.stopPropagation(); if (!disabled) onMouseDown('move', e); }}
      className={`absolute cursor-move group ${
        isSelected ? 'ring-2 ring-brand-blue ring-offset-1' : 'hover:ring-1 hover:ring-gray-400'
      }`}
    >
      {overlay.type === 'signature' ? (
        <img src={overlay.dataUrl} alt="Signature" className="w-full h-full object-contain" draggable={false} />
      ) : (
        <div
          className="w-full h-full flex items-center text-gray-900 font-medium whitespace-nowrap overflow-hidden"
          style={{ fontSize: `${overlay.fontSize * pageSize.h}px` }}
        >
          {overlay.text}
        </div>
      )}

      {/* Remove button */}
      {isSelected && (
        <button
          aria-label="Remove signature item"
          disabled={disabled}
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          className="absolute -top-3 -right-3 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center shadow-sm hover:bg-red-600 z-10"
        >
          <X size={12} />
        </button>
      )}

      {/* Resize handle */}
      {isSelected && (
        <div
          onMouseDown={(e) => { e.stopPropagation(); onMouseDown('resize', e); }}
          className="absolute -bottom-1.5 -right-1.5 w-3.5 h-3.5 bg-brand-blue border-2 border-white rounded-sm cursor-se-resize shadow-sm z-10"
        />
      )}
    </div>
  );
}
